/**
 * MyOpenSearchesWidget — the compact "what am I working on" panel that sits to
 * the LEFT of the dashboard's lifecycle grid, and since Trello #853 takes its
 * height from that grid (`fillHeight`; the row list scrolls).
 *
 * Reuses `GET /api/web/v2/searches-list` under its OWN query key with
 * `page_size=WIDGET_ROWS`.
 *
 * Scope is `owner=me`, `status=open` — STRICTLY Open, not the My Searches
 * page's `open_bd` default (2026-08-13). The panel is titled "My open
 * searches", and Business Development searches are prospects rather than live
 * work, so counting them made the header's total disagree with the sentence
 * above it. That narrowing left this panel's counts out of step with the
 * destination page, whose own default is still Open + BD — so both of this
 * widget's links carry `?status=open` (`SEARCHES_SCOPED_HREF`) and the reader
 * arrives at the list the number described.
 *
 * Each row is id + name + the AgendaGPT action. Client / Status / Days are
 * deliberately absent — the client is almost always the name's prefix, and the
 * widget's own scope is the status. The panel was widened (Trello #853) so the
 * NAME stops truncating, not to make room for more columns.
 *
 * The id is an ANCHOR to the search on Thrive; the name opens Search Detail in
 * this app. Three siblings in a flex row, never nested: a link inside a button
 * is invalid HTML, and nesting is also how the keyboard trap in the searches
 * table happened (that table's `ThriveSearchLink` carries the same two
 * guards for the same reason).
 *
 * AGENDAGPT, NOT PEOPLE LISTS, on the right (Lauren via Jared, 2026-10-07:
 * "Can the home page have AgendaGPT instead of People Lists on the right side
 * of the rows where the inbox icon is"; Jose chose the home widget only). The
 * row's people-lists shortcut (an inbox icon to `/research/imports` filtered by
 * the search) is gone; the action is `SearchAgendaCell`, the My Searches
 * table's own AgendaGPT button — same request, same toast, mailed to the
 * caller — reused, not forked. People Lists stay one click away on the search
 * itself and in the sidebar.
 *
 * The widget calls the searches list by URL and contract only; it imports
 * nothing from the client-engagement feature, the same boundary the imports CTA
 * kept (epic sub-task 03) — which is why the AgendaGPT action lives in
 * `features/agenda/`.
 *
 * Every failure stays INSIDE the panel frame. A dashboard that reflows around a
 * hole because one aggregate 500'd is worse than one that says so in the corner
 * it happened in.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */
import { useMe } from "@nu-assist/shared-fe";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import type { SearchesListResponse } from "@nu-assist/contracts";

import { type SearchesClient, createSearchesClient } from "../../clients/searches.client";
import { SearchAgendaCell } from "../../features/agenda/SearchAgendaCell";
import { thriveSearchCandidatesUrl } from "../../lib/thriveLinks";
import { searchDetailPath } from "../../routing/paths";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

/**
 * Rows on screen. The total in the header is the real one, not this.
 *
 * 12 as of 2026-08-10 (was 10). It is also the request's `page_size`, so
 * changing it changes what the server is asked for, not just what is
 * rendered — there is no client-side slice to fall out of sync.
 */
const WIDGET_ROWS = 12;

export const SEARCHES_HREF = "/client-engagement/searches";

/**
 * The status filter this widget asks for.
 *
 * Named rather than inlined because it appears TWICE — in the request and in
 * the query key — and the two must move together: a key that does not carry the
 * status would serve the previous scope's cached rows to a widget that has
 * since narrowed, which is exactly what a released build would hit on the first
 * load after this change.
 */
const WIDGET_STATUS = "open";

/**
 * Where this panel's own links go — the searches list, PRE-SET to the scope
 * this panel counts.
 *
 * The bare path would land on the list's own `open_bd` default, so "View all 8
 * searches →" reliably opened a list of 14 (PR #674 review I-3). The count is
 * the one place the widget's scope is ASSERTED rather than implied, so the
 * cheapest honest fix is to make the destination agree instead of dropping the
 * number.
 *
 * `SearchesPage` reads `?status=` on mount only; its pills own the filter
 * afterwards, so this seeds the arrival without freezing anything.
 */
const SEARCHES_SCOPED_HREF = `${SEARCHES_HREF}?status=${WIDGET_STATUS}`;

// One client per app session (lazy-resolves `globalThis.fetch` at call time, so
// MSW interception still applies — see the client's note).
const defaultClient = createSearchesClient({ apiBase: API_BASE });

/**
 * The widget's query, exported so the home page's "What's new" carousel reads
 * the SAME cache entry (one request for both) when it links to the caller's
 * most recent search.
 *
 * A key of its own. The My Searches page caches the same endpoint under
 * whatever filters the reader has set; sharing an entry would make this
 * panel show their filtered view, or the page show this panel's ten rows.
 */
export function useMyOpenSearches(client: SearchesClient = defaultClient, enabled = true) {
  return useQuery<SearchesListResponse>({
    queryKey: ["searches", "widget", "my-open", WIDGET_STATUS, WIDGET_ROWS],
    queryFn: () =>
      client.listSearches({
        owner: "me",
        status: WIDGET_STATUS,
        page: 1,
        page_size: WIDGET_ROWS,
      }),
    enabled,
  });
}

export interface MyOpenSearchesWidgetProps {
  /** Injectable for tests (defaults to the module-shared typed client). */
  client?: SearchesClient;
  className?: string;
  /** Injectable caller email for tests; defaults to `useMe()`. */
  meEmailOverride?: string;
  /**
   * Fill the parent's height instead of sizing to the rows: the row list
   * takes the leftover space and scrolls, "View all" stays at the bottom.
   * Needs a parent with a definite height (the dashboard absolutely fills the
   * panel into a wrapper the card grid sizes); anything that still does not
   * fit, such as a long error message, is clipped at the panel's border.
   */
  fillHeight?: boolean;
}

export function MyOpenSearchesWidget({
  client,
  className,
  meEmailOverride,
  fillHeight = false,
}: MyOpenSearchesWidgetProps = {}) {
  const activeClient = client ?? defaultClient;
  const navigate = useNavigate();
  // The AgendaGPT action mails the agenda to the caller. An unresolved session
  // yields "", which disables the button with an explanatory title, as in the
  // My Searches table.
  const me = useMe();
  const meEmail = meEmailOverride ?? (me.status === "authenticated" ? me.data.email : "");

  const query = useMyOpenSearches(activeClient);

  const data = query.data;
  const items = data?.items ?? [];
  // `null` until a response, never `0`: "(0)" before the server answered makes
  // the same unanswered factual claim as an empty row list would.
  const total = data?.total ?? null;
  // This widget is permanently `owner=me`, so `identity_resolved: false` always
  // means the one thing: the caller's email has no `nu_sync.users` match, and
  // the API is telling us it CANNOT know which searches are theirs. Rendering
  // the empty state here would answer a question the server declined to answer
  // — the exact collapse `SearchesPage` was built to avoid, and the reason the
  // API ships the flag at all. Same guided message, shortened for the column.
  const identityUnresolved = data !== undefined && data.identity_resolved === false;

  return (
    <section
      data-testid="my-open-searches"
      className={[
        "flex w-full flex-col rounded-lg border border-border bg-surface",
        // Clips only where the panel is filled to a fixed height (`lg` up);
        // links inside use inset focus rings so the clip never hides them.
        fillHeight ? "lg:overflow-hidden" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <header className="flex items-baseline justify-between gap-2 border-b border-border px-4 py-3">
        <Link
          to={SEARCHES_SCOPED_HREF}
          data-testid="my-open-searches-title"
          className="inline-flex items-center gap-1.5 text-label font-semibold uppercase tracking-wide text-text hover:text-accent-ink rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent"
        >
          {/* The same `Search` glyph `dash-searches` carries in the sidebar,
              imported from the same lucide-react source, so the widget and
              the nav row the footer link leads to are recognisably the same
              destination. Decorative — the link text already names it. */}
          <Search className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          My open searches
        </Link>
        <span data-testid="my-open-searches-count" className="text-label text-text-muted">
          {/* An unresolved identity gets the em dash too. The server sends
              `total: 0` on that path, but "(0)" would assert the same thing the
              body below says we cannot assert. */}
          ({total === null || identityUnresolved ? "—" : total.toLocaleString()})
        </span>
      </header>

      <div className={fillHeight ? "flex min-h-0 flex-1 flex-col" : "flex flex-col"}>
        {query.isError && !data ? (
          <output
            data-testid="my-open-searches-error"
            className="px-4 py-6 text-caption text-text-secondary"
          >
            Couldn’t load your searches just now.
          </output>
        ) : !data ? (
          <output
            data-testid="my-open-searches-loading"
            className="px-4 py-6 text-caption text-text-secondary"
          >
            Loading your searches…
          </output>
        ) : identityUnresolved ? (
          <output
            data-testid="my-open-searches-unresolved-identity"
            className="flex flex-col gap-2 px-4 py-6 text-caption text-text-secondary"
          >
            <span>
              We could not match your account to a Thrive user, so we cannot tell which searches are
              yours. Ask an administrator to link your Thrive account.
            </span>
            {/* Deliberately the BARE path, unlike the other two links here:
                this one appears when we cannot tell which searches are the
                caller's, and it offers the firm's catalog as the way out. The
                panel's Open-only scope is not what the reader is being sent to
                — the full list is. */}
            <Link to={SEARCHES_HREF} className="text-accent-ink hover:underline">
              Browse the firm’s searches →
            </Link>
          </output>
        ) : items.length === 0 ? (
          <output
            data-testid="my-open-searches-empty"
            className="px-4 py-6 text-caption text-text-secondary"
          >
            No open searches.
          </output>
        ) : (
          <>
            {/* Column headers. The row action (People Lists until 2026-10-07,
                AgendaGPT since) has its own `aria-label` and `title`, so hover
                text was never the gap — DISCOVERABILITY was: a bare glyph at
                the end of a row does not announce itself to someone who never
                hovers.
                A header label beats widening the button into an icon+text
                pair, which is what the width actually affords: the label is
                paid for ONCE for the whole column instead of per row, and at
                this width every row is already competing with a truncated
                search name. `aria-hidden` because these are visual column
                labels over controls that each carry their own accessible
                name — announcing "Search / AgendaGPT" again would only add
                noise for a screen-reader user. */}
            <div
              aria-hidden="true"
              data-testid="my-open-searches-columns"
              className="flex items-center gap-1 border-b border-border px-4 pb-1.5 pt-2 text-micro uppercase tracking-[0.02em] text-text-muted"
            >
              <span className="min-w-0 flex-1">Search</span>
              <span className="shrink-0">AgendaGPT</span>
            </div>
            <ul
              data-testid="my-open-searches-rows"
              className={
                fillHeight ? "flex min-h-0 flex-1 flex-col overflow-y-auto" : "flex flex-col"
              }
            >
              {items.map((row) => (
                <li key={row.id_thrive} className="border-b border-border last:border-b-0">
                  {/* Three SIBLINGS, never nested: the Thrive anchor, the
                      Search-Detail button, and the AgendaGPT action. A link
                      inside a button is invalid HTML, and nesting is how the
                      searches table's keyboard trap happened. */}
                  <div className="flex items-center gap-1 px-2">
                    <a
                      href={thriveSearchCandidatesUrl(row.id_thrive)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="my-open-searches-thrive-id"
                      title={`Open search ${row.id_thrive} in Thrive`}
                      // Both guards, matching `SearchesTable`'s
                      // `ThriveSearchLink`. Unlike that table — whose <tr>
                      // owns the click and Enter/Space handlers the anchor has
                      // to escape — no ancestor here handles either event: the
                      // row button is a SIBLING. So these are defensive, not
                      // load-bearing today, and they are here because the
                      // obvious next change to this widget is making the whole
                      // row clickable like the table's, which would silently
                      // reintroduce the keyboard trap epic sub-task 03
                      // documented. Note what they must NOT do:
                      // `preventDefault()` would kill the deep link outright
                      // while every "does not navigate the row" assertion kept
                      // passing (mutation-verified, PR #640 finding C2) —
                      // hence the `defaultPrevented` tests.
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
                      }}
                      className="shrink-0 rounded-md px-1 py-2 text-caption tabular-nums text-text-muted hover:text-accent-ink hover:underline focus:outline-none focus:ring-1 focus:ring-inset focus:ring-accent"
                    >
                      {row.id_thrive}
                    </a>
                    <button
                      type="button"
                      data-testid="my-open-searches-row"
                      data-search-id={row.id_thrive}
                      aria-label={`Open search ${row.name}`}
                      onClick={() => navigate(searchDetailPath(row.id_thrive))}
                      className="flex min-w-0 flex-1 items-center rounded-md px-1 py-2 text-left hover:bg-accent-muted focus:outline-none focus:ring-1 focus:ring-inset focus:ring-accent"
                    >
                      <span className="truncate text-body text-text">{row.name}</span>
                    </button>
                    <span className="shrink-0">
                      <SearchAgendaCell
                        searchId={row.id_thrive}
                        searchName={row.name}
                        meEmail={meEmail}
                        testId="my-open-searches-agenda-gpt"
                      />
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            {/* The number STAYS, and `SEARCHES_SCOPED_HREF` is what earns it.
                This count is of strictly-Open searches while the destination
                page defaults to Open + BD, so on the bare path "View all 14"
                reliably opened a list of a different length (PR #674 review
                I-3). Dropping the number was the other candidate fix and was
                rejected: seeding the destination's filter keeps the more
                useful link AND makes the claim true, where dropping it only
                stopped the page from lying. Do not restore the bare path here
                without also taking the number back out. */}
            <Link
              to={SEARCHES_SCOPED_HREF}
              data-testid="my-open-searches-view-all"
              className="border-t border-border px-4 py-2.5 text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent"
            >
              View all {(total ?? 0).toLocaleString()} {total === 1 ? "search" : "searches"} →
            </Link>
          </>
        )}
      </div>
    </section>
  );
}
